/* eslint-disable max-lines */
import { Button, Input, Modal, Tag, Typography, message as antdMessage } from 'antd';
import { DownloadOutlined, ExpandAltOutlined, FileTextOutlined, SendOutlined } from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';
import DCAIcon from '../../../../../../assets/icons/GenAi/dca-icon.svg';
import { renderMarkdown } from './renderMarkdown';
import { AppSettings } from '../../../../../../AppSettings';
import './SkillsAssistant.less';

export interface ISkillsAssistantMessage {
  role: 'user' | 'assistant';
  text: string;
  skill?: string;
  skillDescription?: string;
  generatedFiles?: string[];
}

const SUGGESTED_PROMPTS = [
  'What agents are active right now?',
  'Show me Clinical Safety AI capabilities',
  'Which skills are in production?',
  'How do I configure LLM routing?',
];

const FALLBACK_RESPONSES: Record<string, string> = {
  agent:
    'There are currently 8 active agents across the platform: Orchestrator, Migration Agent, Foundation Agent, Strategy Agent, SRE Agent, Clinical Safety Agent, Regulatory AI Agent, and AIOps Agent.',
  clinical:
    'Clinical Safety AI is a Live industry solution for Life Sciences. It uses 3 specialised agents — Signal Detection Agent, Evidence Chain Builder, and Regulatory Formatter — plus the MedDRA Coding Engine and PV Database Connector.',
  skill:
    'Currently 44 skills are deployed across 8 categories: Cloud Engineering, DevOps & Platform, App Development, Banking & Finance, Life Sciences, Technology & Ops, Regulatory, and Cross-Industry.',
  llm:
    'The platform uses Claude Opus 4 for critical/multi-step tasks, Claude Sonnet 4 for standard ops, Claude Haiku 3.5 for high-volume lightweight tasks, plus models from OpenAI, Google, Meta, Mistral, AWS, and private fine-tunes. Routing defaults to Auto mode.',
  default:
    'I can help with that. For detailed information, navigate to the relevant section in the DCA Platform — Cloud Enablement for migration lifecycle, AI Engine for model and agent configuration, Industry Solutions for sector-specific capabilities, and Analytics for compliance and performance dashboards.',
};

const fallbackResponse = (msg: string) => {
  const m = msg.toLowerCase();
  if (m.includes('agent') || m.includes('active') || m.includes('running'))
    return FALLBACK_RESPONSES.agent;
  if (m.includes('clinical') || m.includes('safety') || m.includes('pharma'))
    return FALLBACK_RESPONSES.clinical;
  if (m.includes('skill') || m.includes('deployed') || m.includes('production'))
    return FALLBACK_RESPONSES.skill;
  if (m.includes('llm') || m.includes('model') || m.includes('routing') || m.includes('claude'))
    return FALLBACK_RESPONSES.llm;
  return FALLBACK_RESPONSES.default;
};

// Base URL for the DCA AI Skills Engine (chat + downloads).
// Resolved via AppSettings.dcaAiEngine, which already considers both
// REACT_APP_DCA_AI_URL and the per-environment defaults in AppSettings.ts.
// Keeps the localhost convenience fallback when no value is configured.
const ENGINE_BASE: string = (() => {
  const fromAppSettings = AppSettings.dcaAiEngine || '';
  if (fromAppSettings) return fromAppSettings;
  if (
    typeof window !== 'undefined' &&
    window.location &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ) {
    return 'http://127.0.0.1:8765';
  }
  return '';
})();

interface IEngineResponse {
  text?: string;
  skill?: string;
  skill_description?: string;
  generated_files?: string[];
  denied?: boolean;
}

const callEngine = async (
  prompt: string,
  history: ISkillsAssistantMessage[],
): Promise<IEngineResponse> => {
  const resp = await fetch(`${ENGINE_BASE}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt,
      history: history.map((m) => ({ role: m.role, content: m.text })),
      max_iterations: 30,
    }),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`engine ${resp.status}: ${detail || resp.statusText}`);
  }
  return (await resp.json()) as IEngineResponse;
};

// Download a list of workdir-relative paths from the engine as a single zip.
// If `paths` has exactly one entry, the resulting download is still a zip
// (server-side enforces that); the caller can choose to call this with one
// path for single-file or many paths for a bundle.
const downloadGenerated = async (paths: string[], archiveBase: string): Promise<void> => {
  if (!paths || paths.length === 0) return;
  const resp = await fetch(`${ENGINE_BASE}/api/download`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      paths,
      archive_name: archiveBase,
    }),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`download ${resp.status}: ${detail || resp.statusText}`);
  }
  const blob = await resp.blob();
  const cd = resp.headers.get('Content-Disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/i);
  const fname = (match && match[1]) || `${archiveBase}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const stamp = (): string =>
  new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

const SkillsAssistant: React.FC = () => {
  const [messages, setMessages] = useState<ISkillsAssistantMessage[]>([
    {
      role: 'assistant',
      text:
        "Hi! I'm your DCA Skills Assistant, wired to the dca-ai skills engine. Ask me anything — I'll route your request through the matching skill (terraform-developer, aws-cloud-engineer, write-readme, refactor-python, add-unit-tests, …) and return the result here.",
    },
  ]);
  const [input, setInput] = useState<string>('');
  const [typing, setTyping] = useState<boolean>(false);
  const [typingStatus, setTypingStatus] = useState<string>('');
  const [fullOutput, setFullOutput] = useState<ISkillsAssistantMessage | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing]);

  const send = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || typing) return;
    const userMsg: ISkillsAssistantMessage = { role: 'user', text: trimmed };
    const history = messages;
    setMessages([...history, userMsg]);
    setInput('');
    setTyping(true);
    setTypingStatus('Contacting dca-ai engine…');

    callEngine(trimmed, history)
      .then((data) => {
        const prefix = data.denied ? '🚫 Governance denied this request.\n\n' : '';
        const body = data.text || '(no response)';
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: prefix + body,
            skill: data.skill,
            skillDescription: data.skill_description,
            generatedFiles: Array.isArray(data.generated_files) ? data.generated_files : [],
          },
        ]);
      })
      .catch((err: Error) => {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text:
              `Couldn't reach the dca-ai skills engine (${err.message}).\n\n` +
              `Falling back to a cached answer:\n\n${fallbackResponse(trimmed)}`,
          },
        ]);
      })
      .finally(() => {
        setTyping(false);
        setTypingStatus('');
      });
  };

  return (
    <div className="skills-assistant">
      <div className="skills-assistant__header">
        <div className="skills-assistant__title-wrap">
          <img src={DCAIcon} alt="DCA" className="skills-assistant__title-icon" />
          <div>
            <Typography.Title level={5} className="skills-assistant__title">
              DCA Skills Assistant
            </Typography.Title>
            <Typography.Text className="skills-assistant__subtitle">
              <span className="skills-assistant__dot" /> Online · routed via dca-ai skills engine
            </Typography.Text>
          </div>
        </div>
        <Button
          className="skills-assistant__clear-btn"
          onClick={() =>
            setMessages([{ role: 'assistant', text: 'Chat cleared. How can I help?' }])
          }
        >
          Clear Chat
        </Button>
      </div>

      <div className="skills-assistant__body" ref={scrollRef}>
        {messages.map((m, i) => (
          <div
            key={i}
            className={`skills-assistant__row skills-assistant__row--${m.role}`}
          >
            {m.role === 'assistant' && (
              <img src={DCAIcon} alt="bot" className="skills-assistant__avatar" />
            )}
            <div className="skills-assistant__bubble-wrap">
              {m.role === 'assistant' && m.skill && (
                <Tag color="green" className="skills-assistant__skill-tag" title={m.skillDescription}>
                  Using Skill: {m.skill}
                </Tag>
              )}
              <div className={`skills-assistant__bubble skills-assistant__bubble--${m.role}`}>
                {m.role === 'assistant' ? (
                  <div className="skills-assistant__text skills-assistant__text--md">
                    {renderMarkdown(m.text)}
                  </div>
                ) : (
                  <div className="skills-assistant__text">{m.text}</div>
                )}
                {m.role === 'assistant' &&
                  m.generatedFiles &&
                  m.generatedFiles.length > 0 && (
                    <div className="skills-assistant__files">
                      <div className="skills-assistant__files-header">
                        <strong>
                          <FileTextOutlined /> Generated files ({m.generatedFiles.length})
                        </strong>
                        <Button
                          size="small"
                          type="primary"
                          icon={<DownloadOutlined />}
                          loading={downloading === `zip-${i}`}
                          onClick={() => {
                            const id = `zip-${i}`;
                            setDownloading(id);
                            downloadGenerated(
                              m.generatedFiles || [],
                              `dca-ai-output-${stamp()}`,
                            )
                              .catch((err: Error) => {
                                antdMessage.error(`Download failed: ${err.message}`);
                              })
                              .finally(() => setDownloading(null));
                          }}
                        >
                          Download all (.zip)
                        </Button>
                      </div>
                      <ul>
                        {m.generatedFiles.map((f, fi) => {
                          const id = `f-${i}-${fi}`;
                          return (
                            <li key={f}>
                              <span className="skills-assistant__file-name">{f}</span>
                              <Button
                                size="small"
                                type="link"
                                icon={<DownloadOutlined />}
                                loading={downloading === id}
                                onClick={() => {
                                  setDownloading(id);
                                  const base = f.split('/').pop() || 'file';
                                  downloadGenerated([f], base)
                                    .catch((err: Error) => {
                                      antdMessage.error(`Download failed: ${err.message}`);
                                    })
                                    .finally(() => setDownloading(null));
                                }}
                              >
                                Download
                              </Button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                {m.role === 'assistant' && m.text && m.text.length > 0 && (
                  <div className="skills-assistant__bubble-actions">
                    <Button
                      size="small"
                      type="link"
                      icon={<ExpandAltOutlined />}
                      onClick={() => setFullOutput(m)}
                    >
                      View full output
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {typing && (
          <div className="skills-assistant__row skills-assistant__row--assistant">
            <img src={DCAIcon} alt="bot" className="skills-assistant__avatar" />
            <div className="skills-assistant__bubble skills-assistant__bubble--assistant skills-assistant__typing">
              <span className="skills-assistant__dot-anim" />
              <span className="skills-assistant__dot-anim" />
              <span className="skills-assistant__dot-anim" />
              {typingStatus && (
                <span className="skills-assistant__typing-status">{typingStatus}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="skills-assistant__suggestions">
          {SUGGESTED_PROMPTS.map((s) => (
            <Button
              key={s}
              size="small"
              className="skills-assistant__suggestion-btn"
              onClick={() => send(s)}
            >
              {s}
            </Button>
          ))}
        </div>
      )}

      <div className="skills-assistant__input-row">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onPressEnter={() => send(input)}
          placeholder="Ask the Skills Assistant…"
          className="skills-assistant__input"
          disabled={typing}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={() => send(input)}
          disabled={!input.trim() || typing}
          className="skills-assistant__send-btn"
        >
          Send
        </Button>
      </div>

      <Modal
        visible={!!fullOutput}
        onCancel={() => setFullOutput(null)}
        footer={[
          fullOutput && fullOutput.generatedFiles && fullOutput.generatedFiles.length > 0 ? (
            <Button
              key="dl"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                if (!fullOutput || !fullOutput.generatedFiles) return;
                downloadGenerated(
                  fullOutput.generatedFiles,
                  `dca-ai-output-${stamp()}`,
                ).catch((err: Error) => {
                  antdMessage.error(`Download failed: ${err.message}`);
                });
              }}
            >
              Download all files (.zip)
            </Button>
          ) : null,
          <Button key="close" onClick={() => setFullOutput(null)}>
            Close
          </Button>,
        ]}
        width={900}
        title={
          fullOutput && fullOutput.skill ? (
            <span>
              <Tag color="green">Using Skill: {fullOutput.skill}</Tag>
              Full output
            </span>
          ) : (
            'Full output'
          )
        }
        destroyOnClose
      >
        {fullOutput && (
          <div
            className="skills-assistant__text--md"
            style={{
              maxHeight: '70vh',
              overflow: 'auto',
              background: '#ffffff',
              padding: '0.5rem 0.25rem',
            }}
          >
            {renderMarkdown(fullOutput.text)}
            {fullOutput.generatedFiles && fullOutput.generatedFiles.length > 0 && (
              <div className="skills-assistant__files" style={{ marginTop: 16 }}>
                <strong>
                  <FileTextOutlined /> Generated files ({fullOutput.generatedFiles.length})
                </strong>
                <ul>
                  {fullOutput.generatedFiles.map((f) => (
                    <li key={f}>
                      <span className="skills-assistant__file-name">{f}</span>
                      <Button
                        size="small"
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() => {
                          const base = f.split('/').pop() || 'file';
                          downloadGenerated([f], base).catch((err: Error) => {
                            antdMessage.error(`Download failed: ${err.message}`);
                          });
                        }}
                      >
                        Download
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SkillsAssistant;
